import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { motion } from 'motion/react';

import {
  kaizenMicroTransition,
  kaizenNavTransition
} from '../lib/kaizenMotion';

import { useProjects } from '../contexts/ProjectContext';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useRealtime } from '../contexts/RealtimeContext';

import { FolderBrowserModal } from '../components/modals/FolderBrowserModal';

import {
  FolderKanban,
  Plus,
  ArrowRight,
  Shield,
  Globe,
  Laptop,
  AlertTriangle,
  Loader2,
  FolderOpen,
  X,
  Terminal,
  Cloud
} from 'lucide-react';


const createInitialForm = () => ({

  name: '',
  description: '',
  localPath: '',
  buildCommand: 'npm run build',
  testCommand: 'npm test',
  buildOutputDirectory: 'dist',
  cloudflareProjectName: '',
  previewEnabled: true,
  productionEnabled: false
});


import {
  EditProjectModal
} from '../components/modals/EditProjectModal';

export const ProjectsPage: React.FC = () => {

  // KAIZEN_PROJECT_EDIT_STATE_V1
  const [
    editingProjectId,
    setEditingProjectId
  ] =
    useState<string | null>(
      null
    );


  const {
    updateProject
  } =
    useProjects();



  const navigate =
    useNavigate();


  const {
    projects,
    selectProject,
    addProject
  } =
    useProjects();


  const {
    displayPath
  } =
    usePrivacy();


  const {
    device,
    deviceStatus
  } =
    useRealtime();


  const [
    isAdding,
    setIsAdding
  ] =
    useState(
      false
    );


  const [
    isSaving,
    setIsSaving
  ] =
    useState(
      false
    );


  const [
    isFolderBrowserOpen,
    setIsFolderBrowserOpen
  ] =
    useState(
      false
    );


  const [
    saveError,
    setSaveError
  ] =
    useState<string | null>(
      null
    );


  const [
    formData,
    setFormData
  ] =
    useState(
      createInitialForm()
    );


  const closeModal =
    () => {

      if (
        isSaving
      ) {
        return;
      }


      setIsAdding(
        false
      );

      setSaveError(
        null
      );
    };


  const handleSubmit =
    async (
      event:
        React.FormEvent
    ) => {

      event.preventDefault();


      if (
        !formData.name.trim() ||
        !formData.localPath.trim()
      ) {

        setSaveError(
          'Project name and laptop folder are required.'
        );

        return;
      }


      if (
        !device?.id
      ) {

        setSaveError(
          'No registered KAIZEN laptop was found. Start the KAIZEN Remote Agent first.'
        );

        return;
      }


      setIsSaving(
        true
      );

      setSaveError(
        null
      );


      try {

        const created =
          await addProject({

            deviceId:
              device.id,

            name:
              formData.name.trim(),

            description:
              formData.description.trim(),

            localPath:
              formData.localPath.trim(),

            projectType:
              'vite',

            buildCommand:
              formData.buildCommand.trim() ||
              'npm run build',

            testCommand:
              formData.testCommand.trim() ||
              undefined,

            buildOutputDirectory:
              formData.buildOutputDirectory.trim() ||
              'dist',

            deploymentProvider:
              'cloudflare_pages',

            cloudflareProjectName:
              formData.cloudflareProjectName.trim() ||
              undefined,

            previewEnabled:
              formData.previewEnabled,

            productionEnabled:
              formData.productionEnabled
          });


        selectProject(
          created.id
        );


        setFormData(
          createInitialForm()
        );


        setIsAdding(
          false
        );


        navigate(
          `/projects/${created.id}`
        );

      }
      catch (
        error:
          any
      ) {

        setSaveError(
          error?.message ||
          'Project registration failed.'
        );

      }
      finally {

        setIsSaving(
          false
        );
      }
    };


  return (

    <div className="space-y-4 md:space-y-5">

      {/* HEADER */}

      <div
        className="
          flex items-center justify-between
          gap-3
          px-0.5
        "
      >

        <div className="min-w-0">

          <div className="flex items-center gap-2">

            <FolderKanban className="w-5 h-5 text-indigo-400" />

            <h2 className="text-lg sm:text-xl font-semibold text-slate-100 tracking-tight">
              Projects
            </h2>

          </div>

          <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5">
            Registered development workspaces on your personal laptop
          </p>

        </div>


        <motion.button
          type="button"
          onClick={() => {

            setSaveError(
              null
            );

            setIsAdding(
              true
            );
          }}
          whileHover={{
            y: -1
          }}
          whileTap={{
            scale: 0.97
          }}
          transition={
            kaizenMicroTransition
          }
          className="
            shrink-0
            min-h-[40px]
            inline-flex items-center justify-center
            gap-1.5
            px-3.5
            rounded-lg
            border border-indigo-400/25
            bg-indigo-500/90
            hover:bg-indigo-500
            text-white
            text-[10px] sm:text-xs
            font-semibold
            shadow-[0_10px_30px_rgba(79,70,229,0.18)]
          "
        >

          <Plus className="w-3.5 h-3.5" />

          <span className="hidden sm:inline">
            Register Project
          </span>

          <span className="sm:hidden">
            Register
          </span>

        </motion.button>

      </div>


      {/* OFFLINE */}

      {deviceStatus !== 'online' && (

        <div
          className="
            flex items-start gap-3
            px-3.5 py-3
            rounded-xl
            border border-amber-400/15
            bg-amber-400/[0.07]
            shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]
          "
        >

          <div
            className="
              w-8 h-8
              shrink-0
              rounded-lg
              bg-amber-400/10
              text-amber-300
              flex items-center justify-center
            "
          >

            <AlertTriangle className="w-4 h-4" />

          </div>


          <div>

            <div className="text-xs font-semibold text-amber-200">
              Personal laptop offline
            </div>

            <p className="text-[10px] sm:text-[11px] text-amber-100/60 mt-0.5">
              Existing project profiles remain available. Remote validation requires the Agent.
            </p>

          </div>

        </div>

      )}


      {/* PROJECTS */}

      {projects.length === 0 ? (

        <div
          className="
            min-h-[300px]
            rounded-[18px]
            border border-white/10
            bg-slate-900/82
            shadow-[0_28px_90px_rgba(2,6,23,0.35)]
            backdrop-blur-2xl
            flex flex-col items-center justify-center
            text-center
            px-6
          "
        >

          <div
            className="
              w-11 h-11
              rounded-xl
              border border-indigo-400/20
              bg-indigo-400/10
              text-indigo-300
              flex items-center justify-center
            "
          >

            <FolderKanban className="w-5 h-5" />

          </div>


          <h3 className="mt-3 text-sm font-semibold text-slate-200">
            No projects registered
          </h3>

          <p className="mt-1 text-[10px] sm:text-xs text-slate-500 max-w-sm">
            Register a repository on the personal laptop to begin remote builds and controlled deployments.
          </p>

        </div>

      ) : (

        <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3.5">

          {projects.map(
            project => (

              <motion.article
                key={project.id}
                layout
                initial={{
                  opacity: 0,
                  y: 12,
                  scale: 0.992
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }}
                whileHover={{
                  y: -4,
                  scale: 1.006
                }}
                transition={
                  kaizenNavTransition
                }
                className="
                  group
                  relative
                  overflow-hidden
                  rounded-[18px]
                  border border-white/[0.08]
                  bg-slate-900/72
                  shadow-[0_22px_70px_rgba(2,6,23,0.25)]
                  backdrop-blur-2xl
                  transition-colors
                  hover:border-indigo-400/25
                  hover:bg-slate-900/82
                "
              >

                <div
                  aria-hidden="true"
                  className="
                    pointer-events-none
                    absolute
                    -top-20 -right-20
                    w-48 h-48
                    rounded-full
                    bg-indigo-500/[0.055]
                    blur-3xl
                  "
                />


                <div
                  className="
                    relative
                    flex items-center justify-between
                    gap-3
                    px-4
                    py-3.5
                    border-b border-white/[0.07]
                    bg-white/[0.025]
                  "
                >

                  <div className="flex items-center gap-3 min-w-0">

                    <div
                      className="
                        w-9 h-9
                        shrink-0
                        rounded-xl
                        border border-indigo-400/20
                        bg-indigo-400/10
                        text-indigo-300
                        flex items-center justify-center
                        shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]
                      "
                    >

                      <FolderKanban className="w-[18px] h-[18px]" />

                    </div>


                    <div className="min-w-0">

                      <h3 className="text-sm font-semibold text-slate-100 truncate">
                        {project.name}
                      </h3>

                      <p className="text-[10px] text-slate-500 truncate mt-0.5">
                        {project.description || 'Development profile'}
                      </p>

                    </div>

                  </div>


                  {project.productionEnabled && (

                    <span
                      className="
                        shrink-0
                        inline-flex items-center gap-1
                        px-2 py-1
                        rounded-md
                        border border-emerald-400/15
                        bg-emerald-400/[0.07]
                        text-[9px]
                        text-emerald-300
                      "
                    >

                      <Shield className="w-3 h-3" />

                      Production Enabled

                    </span>

                  )}

                </div>


                <div className="relative p-3.5 sm:p-4 space-y-3">

                  <div
                    className="
                      overflow-hidden
                      rounded-xl
                      border border-white/[0.055]
                      bg-[#020817]/28
                      shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]
                    "
                  >

                    <div className="px-3.5 py-2.5 border-b border-white/[0.055]">

                      <span className="block text-[9px] text-slate-600 mb-1">
                        Laptop Folder
                      </span>

                      <span
                        className="
                          block
                          truncate
                          font-mono
                          text-[10px]
                          text-slate-300
                        "
                        title={
                          project.localPath
                        }
                      >
                        {displayPath(
                          project.localPath
                        )}
                      </span>

                    </div>


                    <div
                      className="
                        flex items-center gap-3
                        px-3.5 py-2.5
                        border-b border-white/[0.055]
                      "
                    >

                      <Terminal className="w-3.5 h-3.5 shrink-0 text-indigo-400" />

                      <span className="text-[10px] text-slate-500">
                        Build
                      </span>

                      <span className="ml-auto truncate font-mono text-[10px] text-slate-300">
                        {project.buildCommand}
                      </span>

                    </div>


                    <div className="flex items-center gap-3 px-3.5 py-2.5">

                      <Cloud className="w-3.5 h-3.5 shrink-0 text-indigo-400" />

                      <span className="text-[10px] text-slate-500">
                        {project.config?.deployment?.mode === 'vercel_render'
                          ? 'Deployment'
                          : 'Cloudflare'}
                      </span>

                      <span className="ml-auto truncate font-mono text-[10px] text-slate-300">
                        {project.config?.deployment?.mode === 'vercel_render'
                          ? 'Vercel + Render'
                          : (
                              project.cloudflareProjectName ||
                              'Not configured'
                            )}
                      </span>

                    </div>

                  </div>


                  <div className="flex items-center justify-between gap-3">

                    {project.productionUrl ? (

                      <a
                        href={project.productionUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="
                          min-h-[38px]
                          inline-flex items-center
                          gap-1.5
                          text-[10px]
                          font-medium
                          text-indigo-300
                        "
                      >

                        <Globe className="w-3.5 h-3.5" />

                        Live Site

                      </a>

                    ) : (

                      <span className="text-[10px] text-slate-600">
                        Site not configured
                      </span>

                    )}


                                        {/* KAIZEN_PROJECT_EDIT_BUTTON_V1 */}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingProjectId(
                          project.id
                        );
                      }}
                      className="
                        min-h-[38px]
                        inline-flex
                        items-center
                        justify-center
                        gap-1.5
                        px-3
                        rounded-lg
                        border
                        border-white/[0.07]
                        bg-white/[0.025]
                        hover:bg-white/[0.05]
                        text-[10px]
                        font-medium
                        text-slate-300
                        transition-colors
                        active:scale-[0.98]
                      "
                    >
                      Edit
                    </button>

<button
                      type="button"
                      onClick={() => {

                        selectProject(
                          project.id
                        );

                        navigate(
                          `/projects/${project.id}`
                        );
                      }}
                      className="
                        min-h-[38px]
                        inline-flex items-center justify-center
                        gap-1.5
                        px-3
                        rounded-lg
                        border border-white/[0.07]
                        bg-white/[0.025]
                        hover:bg-white/[0.05]
                        text-[10px]
                        font-medium
                        text-slate-300
                      "
                    >

                      Manage

                      <ArrowRight className="w-3.5 h-3.5 text-indigo-400 transition-transform duration-200 group-hover:translate-x-0.5" />

                    </button>

                  </div>

                </div>

              </motion.article>

            )
          )}

        </div>

      )}


      {/* KAIZEN_PROJECT_EDIT_MODAL_V1 */}
      {editingProjectId && (() => {

        const editingProject =
          projects.find(
            project =>
              project.id ===
              editingProjectId
          );


        if (
          !editingProject
        ) {

          return null;
        }


        return (

          <EditProjectModal
            project={
              editingProject
            }
            onClose={() => {
              setEditingProjectId(
                null
              );
            }}
            onSave={async updates => {

              await updateProject(
                editingProject.id,
                updates
              );


              setEditingProjectId(
                null
              );
            }}
          />

        );
      })()}

      {/* ==================================================
          REGISTER PROJECT
      ================================================== */}

      {isAdding && (

        <div
          className="
            fixed inset-0 z-[80]
            flex items-end sm:items-center justify-center
            p-0 sm:p-4
            bg-slate-950/60
            backdrop-blur-xl
          "
        >

          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute
              left-1/2 top-[15%]
              -translate-x-1/2
              w-72 h-72
              rounded-full
              bg-indigo-500/10
              blur-3xl
            "
          />


          <div
            className="
              relative
              w-full
              sm:max-w-xl
              max-h-[92dvh]
              flex flex-col
              overflow-hidden
              rounded-t-[20px] sm:rounded-[18px]
              border border-white/10
              bg-slate-900/88
              shadow-[0_28px_90px_rgba(2,6,23,0.72)]
              backdrop-blur-2xl
            "
          >

            <div
              className="
                shrink-0
                flex items-center justify-between
                gap-3
                px-4 sm:px-5
                py-4
                border-b border-white/[0.07]
                bg-white/[0.025]
              "
            >

              <div className="flex items-center gap-3">

                <div
                  className="
                    w-9 h-9
                    rounded-xl
                    border border-indigo-400/20
                    bg-indigo-400/10
                    text-indigo-300
                    flex items-center justify-center
                  "
                >

                  <FolderKanban className="w-[18px] h-[18px]" />

                </div>


                <div>

                  <h3 className="text-[15px] font-semibold text-slate-100">
                    Register Development Project
                  </h3>

                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Create a remote development profile
                  </p>

                </div>

              </div>


              <button
                type="button"
                onClick={
                  closeModal
                }
                className="
                  w-9 h-9
                  rounded-lg
                  text-slate-500
                  hover:text-slate-200
                  hover:bg-white/[0.05]
                  flex items-center justify-center
                "
              >

                <X className="w-4 h-4" />

              </button>

            </div>


            <form
              onSubmit={
                handleSubmit
              }
              className="
                flex-1
                min-h-0
                overflow-y-auto
                px-4 sm:px-5
                py-4
                space-y-4
                text-xs
              "
            >

              {saveError && (

                <div
                  className="
                    flex items-start gap-3
                    px-3.5 py-3
                    rounded-xl
                    border border-rose-400/15
                    bg-rose-400/[0.07]
                    text-rose-300
                  "
                >

                  <AlertTriangle className="w-4 h-4 shrink-0" />

                  <span>
                    {saveError}
                  </span>

                </div>

              )}


              <div
                className="
                  flex items-center gap-3
                  px-3.5 py-3
                  rounded-xl
                  border border-white/[0.07]
                  bg-white/[0.025]
                "
              >

                <Laptop className="w-4 h-4 text-indigo-400" />


                <div className="min-w-0">

                  <span className="block text-[9px] text-slate-600">
                    Target Laptop
                  </span>

                  <span className="block truncate text-[11px] text-slate-300 mt-0.5">
                    {device?.name || 'No registered host'}
                  </span>

                </div>


                <span
                  className={`ml-auto text-[9px] font-medium ${
                    deviceStatus === 'online'
                      ? 'text-emerald-300'
                      : 'text-slate-600'
                  }`}
                >
                  {deviceStatus === 'online'
                    ? 'ONLINE'
                    : 'OFFLINE'}
                </span>

              </div>


              <div>

                <label className="block text-[10px] text-slate-500 mb-1.5">
                  Project Name
                </label>

                <input
                  type="text"
                  required
                  autoFocus
                  value={
                    formData.name
                  }
                  placeholder="e.g. BCC Review Suite"
                  onChange={
                    event =>
                      setFormData({
                        ...formData,
                        name:
                          event.target.value
                      })
                  }
                  className="
                    w-full
                    min-h-[44px]
                    px-3 py-2
                    rounded-lg
                    border border-white/[0.08]
                    bg-[#020817]/45
                    text-slate-200
                    focus:border-indigo-400/60
                    focus:ring-1 focus:ring-indigo-500/30
                  "
                />

              </div>


              <div>

                <label className="block text-[10px] text-slate-500 mb-1.5">
                  Description
                </label>

                <input
                  type="text"
                  value={
                    formData.description
                  }
                  placeholder="Optional description"
                  onChange={
                    event =>
                      setFormData({
                        ...formData,
                        description:
                          event.target.value
                      })
                  }
                  className="
                    w-full
                    min-h-[44px]
                    px-3 py-2
                    rounded-lg
                    border border-white/[0.08]
                    bg-[#020817]/45
                    text-slate-200
                  "
                />

              </div>


              <div>

                <div className="flex items-center justify-between mb-1.5">

                  <label className="text-[10px] text-slate-500">
                    Laptop Folder
                  </label>

                  <span className="text-[9px] text-slate-600">
                    Any accessible local drive
                  </span>

                </div>


                <div className="flex gap-2">

                  <input
                    type="text"
                    required
                    spellCheck={
                      false
                    }
                    value={
                      formData.localPath
                    }
                    placeholder={'D:\\Enabling Work Module\\project'}
                    onChange={
                      event =>
                        setFormData({
                          ...formData,
                          localPath:
                            event.target.value
                        })
                    }
                    className="
                      min-w-0
                      flex-1
                      min-h-[44px]
                      px-3 py-2
                      rounded-lg
                      border border-white/[0.08]
                      bg-[#020817]/45
                      font-mono
                      text-[11px]
                      text-slate-200
                    "
                  />


                  <button
                    type="button"
                    disabled={
                      deviceStatus !== 'online' ||
                      isSaving
                    }
                    onClick={() =>
                      setIsFolderBrowserOpen(
                        true
                      )
                    }
                    className="
                      w-11 h-11
                      shrink-0
                      rounded-lg
                      border border-indigo-400/15
                      bg-indigo-400/[0.07]
                      text-indigo-300
                      flex items-center justify-center
                      disabled:opacity-40
                    "
                  >

                    <FolderOpen className="w-4 h-4" />

                  </button>

                </div>

              </div>


              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                <div>

                  <label className="block text-[10px] text-slate-500 mb-1.5">
                    Build Command
                  </label>

                  <input
                    type="text"
                    value={
                      formData.buildCommand
                    }
                    onChange={
                      event =>
                        setFormData({
                          ...formData,
                          buildCommand:
                            event.target.value
                        })
                    }
                    className="
                      w-full min-h-[44px]
                      px-3 py-2
                      rounded-lg
                      border border-white/[0.08]
                      bg-[#020817]/45
                      font-mono text-xs
                      text-slate-200
                    "
                  />

                </div>


                <div>

                  <label className="block text-[10px] text-slate-500 mb-1.5">
                    Test Command
                  </label>

                  <input
                    type="text"
                    value={
                      formData.testCommand
                    }
                    onChange={
                      event =>
                        setFormData({
                          ...formData,
                          testCommand:
                            event.target.value
                        })
                    }
                    className="
                      w-full min-h-[44px]
                      px-3 py-2
                      rounded-lg
                      border border-white/[0.08]
                      bg-[#020817]/45
                      font-mono text-xs
                      text-slate-200
                    "
                  />

                </div>

              </div>


              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                <div>

                  <label className="block text-[10px] text-slate-500 mb-1.5">
                    Build Output
                  </label>

                  <input
                    type="text"
                    value={
                      formData.buildOutputDirectory
                    }
                    onChange={
                      event =>
                        setFormData({
                          ...formData,
                          buildOutputDirectory:
                            event.target.value
                        })
                    }
                    className="
                      w-full min-h-[44px]
                      px-3 py-2
                      rounded-lg
                      border border-white/[0.08]
                      bg-[#020817]/45
                      font-mono text-xs
                      text-slate-200
                    "
                  />

                </div>


                <div>

                  <label className="block text-[10px] text-slate-500 mb-1.5">
                    Cloudflare Project
                  </label>

                  <input
                    type="text"
                    value={
                      formData.cloudflareProjectName
                    }
                    placeholder="Optional"
                    onChange={
                      event =>
                        setFormData({
                          ...formData,
                          cloudflareProjectName:
                            event.target.value
                        })
                    }
                    className="
                      w-full min-h-[44px]
                      px-3 py-2
                      rounded-lg
                      border border-white/[0.08]
                      bg-[#020817]/45
                      font-mono text-xs
                      text-slate-200
                    "
                  />

                </div>

              </div>


              <div
                className="
                  sticky bottom-0
                  -mx-4 sm:-mx-5
                  px-4 sm:px-5
                  pt-3
                  pb-[calc(0.25rem+env(safe-area-inset-bottom))]
                  flex items-center justify-between
                  gap-3
                  border-t border-white/[0.07]
                  bg-slate-900/92
                  backdrop-blur-xl
                "
              >

                <button
                  type="button"
                  disabled={
                    isSaving
                  }
                  onClick={
                    closeModal
                  }
                  className="
                    min-h-[44px]
                    px-4
                    rounded-lg
                    text-xs
                    text-slate-400
                    hover:text-slate-100
                    hover:bg-white/[0.045]
                  "
                >
                  Cancel
                </button>


                <button
                  type="submit"
                  disabled={
                    isSaving ||
                    !device?.id
                  }
                  className="
                    min-h-[44px]
                    px-5
                    rounded-lg
                    border border-indigo-400/25
                    bg-indigo-500/90
                    hover:bg-indigo-500
                    disabled:opacity-40
                    text-white
                    text-xs
                    font-semibold
                    flex items-center justify-center
                    gap-2
                    shadow-[0_10px_30px_rgba(79,70,229,0.22)]
                  "
                >

                  {isSaving && (

                    <Loader2 className="w-4 h-4 animate-spin" />

                  )}

                  {isSaving
                    ? 'Registering…'
                    : 'Register Project'}

                </button>

              </div>

            </form>

          </div>

        </div>

      )}


      <FolderBrowserModal
        isOpen={
          isFolderBrowserOpen
        }
        onClose={() =>
          setIsFolderBrowserOpen(
            false
          )
        }
        onSelect={
          selectedPath => {

            setFormData(
              previous => ({
                ...previous,
                localPath:
                  selectedPath
              })
            );


            setIsFolderBrowserOpen(
              false
            );
          }
        }
      />

    </div>
  );
};